import {
  calculateRefreshTiming,
  isTokenExpired,
  getTimeUntilExpiration,
  getCombinedTokenInfo,
} from '@/utils/jwt'
import type { RefreshTokenResponse, CSRFTokenInfo } from '@/types/api'
import { csrfService } from '@/services/csrfService'

export interface TokenRefreshConfig {
  refreshMinutesBefore: number // Minutes before expiration to refresh
  maxRetries: number // Maximum retry attempts
  retryDelayMs: number // Delay between retries
  enableLogging: boolean // Enable debug logging
}

export interface TokenRefreshCallbacks {
  onRefreshSuccess: (response: RefreshTokenResponse) => Promise<void>
  onRefreshFailure: (error: Error) => Promise<void>
  onRefreshAttempt: () => void
  refreshTokenApi: (refreshToken: string) => Promise<RefreshTokenResponse>
  onCSRFTokenUpdate?: (csrfToken: string) => void
}

export class TokenRefreshService {
  private config: TokenRefreshConfig
  private callbacks: TokenRefreshCallbacks
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private isRefreshing = false
  private refreshPromise: Promise<RefreshTokenResponse> | null = null
  private isActive = false

  constructor(config: Partial<TokenRefreshConfig>, callbacks: TokenRefreshCallbacks) {
    this.config = {
      refreshMinutesBefore: 10,
      maxRetries: 3,
      retryDelayMs: 1000,
      enableLogging: false,
      ...config,
    }
    this.callbacks = callbacks
  }

  /**
   * Start automatic token refresh monitoring
   */
  start(token: string, refreshToken: string): void {
    if (this.isActive) {
      this.stop()
    }

    this.isActive = true
    this.scheduleRefresh(token, refreshToken)

    if (this.config.enableLogging) {
      console.log('[TokenRefresh] Service started')
    }
  }

  /**
   * Stop automatic token refresh monitoring
   */
  stop(): void {
    this.isActive = false

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    this.isRefreshing = false
    this.refreshPromise = null

    if (this.config.enableLogging) {
      console.log('[TokenRefresh] Service stopped')
    }
  }

  /**
   * Schedule the next token refresh
   */
  private scheduleRefresh(token: string, refreshToken: string): void {
    if (!this.isActive) return

    const jwtTiming = calculateRefreshTiming(token, this.config.refreshMinutesBefore)

    // Check CSRF token timing if CSRF is enabled
    let csrfTiming = { shouldRefresh: false, timeUntilRefresh: Infinity }
    if (csrfService.isEnabled()) {
      const csrfTokenInfo = csrfService.getStoredTokenInfo()
      if (csrfTokenInfo && csrfService.shouldRefreshToken()) {
        csrfTiming.shouldRefresh = true
        csrfTiming.timeUntilRefresh = 0
      } else if (csrfTokenInfo?.expiresAt) {
        const refreshTime = csrfTokenInfo.expiresAt - this.config.refreshMinutesBefore * 60 * 1000
        csrfTiming.timeUntilRefresh = Math.max(0, refreshTime - Date.now())
      }
    }

    // Use the earlier of JWT or CSRF refresh times
    const shouldRefresh = jwtTiming.shouldRefresh || csrfTiming.shouldRefresh
    const timeUntilRefresh = Math.min(jwtTiming.timeUntilRefresh, csrfTiming.timeUntilRefresh)

    if (this.config.enableLogging) {
      console.log('[TokenRefresh] Scheduling refresh:', {
        jwt: {
          shouldRefresh: jwtTiming.shouldRefresh,
          timeUntilRefresh: jwtTiming.timeUntilRefresh,
          timeUntilExpiration: jwtTiming.timeUntilExpiration,
        },
        csrf: {
          shouldRefresh: csrfTiming.shouldRefresh,
          timeUntilRefresh: csrfTiming.timeUntilRefresh,
        },
        combined: {
          shouldRefresh,
          timeUntilRefresh,
        },
      })
    }

    if (shouldRefresh) {
      // Token needs immediate refresh
      this.performRefresh(refreshToken)
    } else if (timeUntilRefresh > 0 && timeUntilRefresh !== Infinity) {
      // Schedule refresh for later
      this.refreshTimer = setTimeout(() => {
        if (this.isActive) {
          this.performRefresh(refreshToken)
        }
      }, timeUntilRefresh)
    }
  }

  /**
   * Perform token refresh with retry logic
   */
  private async performRefresh(refreshToken: string, attempt: number = 1): Promise<void> {
    if (!this.isActive || this.isRefreshing) {
      return
    }

    // If already refreshing, return the existing promise
    if (this.refreshPromise) {
      try {
        await this.refreshPromise
        return
      } catch (error) {
        // Continue with new refresh attempt
      }
    }

    this.isRefreshing = true
    this.callbacks.onRefreshAttempt()

    if (this.config.enableLogging) {
      console.log(
        `[TokenRefresh] Attempting refresh (attempt ${attempt}/${this.config.maxRetries})`,
      )
    }

    this.refreshPromise = this.callbacks.refreshTokenApi(refreshToken)

    try {
      const response = await this.refreshPromise

      if (this.config.enableLogging) {
        console.log('[TokenRefresh] Refresh successful')
      }

      await this.callbacks.onRefreshSuccess(response)

      // Handle CSRF token update if present
      if (response.csrf_token && csrfService.isEnabled()) {
        csrfService.storeToken(response.csrf_token)
        this.callbacks.onCSRFTokenUpdate?.(response.csrf_token)

        if (this.config.enableLogging) {
          console.log('[TokenRefresh] CSRF token updated')
        }
      }

      // Schedule next refresh with new token
      this.scheduleRefresh(response.token, response.refresh_token)
    } catch (error) {
      if (this.config.enableLogging) {
        console.error(`[TokenRefresh] Refresh failed (attempt ${attempt}):`, error)
      }

      if (attempt < this.config.maxRetries) {
        // Retry after delay
        setTimeout(() => {
          if (this.isActive) {
            this.performRefresh(refreshToken, attempt + 1)
          }
        }, this.config.retryDelayMs * attempt) // Exponential backoff
      } else {
        // Max retries reached, handle failure
        await this.callbacks.onRefreshFailure(error as Error)
        this.stop()
      }
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  /**
   * Force immediate token refresh
   */
  async forceRefresh(refreshToken: string): Promise<RefreshTokenResponse> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    if (this.config.enableLogging) {
      console.log('[TokenRefresh] Force refresh requested')
    }

    this.isRefreshing = true
    this.callbacks.onRefreshAttempt()

    try {
      this.refreshPromise = this.callbacks.refreshTokenApi(refreshToken)
      const response = await this.refreshPromise

      await this.callbacks.onRefreshSuccess(response)

      // Handle CSRF token update if present
      if (response.csrf_token && csrfService.isEnabled()) {
        csrfService.storeToken(response.csrf_token)
        this.callbacks.onCSRFTokenUpdate?.(response.csrf_token)

        if (this.config.enableLogging) {
          console.log('[TokenRefresh] CSRF token updated (force refresh)')
        }
      }

      // Reschedule with new token
      if (this.isActive) {
        this.scheduleRefresh(response.token, response.refresh_token)
      }

      return response
    } catch (error) {
      await this.callbacks.onRefreshFailure(error as Error)
      throw error
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  /**
   * Check if service is currently refreshing
   */
  get isCurrentlyRefreshing(): boolean {
    return this.isRefreshing
  }

  /**
   * Check if service is active
   */
  get isServiceActive(): boolean {
    return this.isActive
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<TokenRefreshConfig>): void {
    this.config = { ...this.config, ...newConfig }

    if (this.config.enableLogging) {
      console.log('[TokenRefresh] Configuration updated:', this.config)
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): TokenRefreshConfig {
    return { ...this.config }
  }

  /**
   * Get service status
   */
  getStatus(): {
    isActive: boolean
    isRefreshing: boolean
    hasScheduledRefresh: boolean
  } {
    return {
      isActive: this.isActive,
      isRefreshing: this.isRefreshing,
      hasScheduledRefresh: this.refreshTimer !== null,
    }
  }
}

/**
 * Create a token refresh service instance
 */
export function createTokenRefreshService(
  config: Partial<TokenRefreshConfig> = {},
  callbacks: TokenRefreshCallbacks,
): TokenRefreshService {
  return new TokenRefreshService(config, callbacks)
}
